/**
 * A scheduled trigger starts one flow definition on a schedule. It is pinned to that definition
 * rather than to whatever the flow has published, so a schedule keeps running the version it was
 * pointed at until someone moves it.
 */
import { OneOrManySchema, QueryBooleanSchema } from 'notable-common';
import { z } from 'zod';
import { FlowScheduleSpecSchema } from './scheduleSpec.js';
import { FlowApiPageLimitSchema, FlowApiPageOffsetSchema } from '../pagination/pageLimit.js';
/** What to do when a run is due while the last one is still going. Temporal's policies. */
export var FlowScheduleOverlapPolicy;
(function (FlowScheduleOverlapPolicy) {
    FlowScheduleOverlapPolicy["Skip"] = "SKIP";
    FlowScheduleOverlapPolicy["BufferOne"] = "BUFFER_ONE";
    FlowScheduleOverlapPolicy["BufferAll"] = "BUFFER_ALL";
    FlowScheduleOverlapPolicy["CancelOther"] = "CANCEL_OTHER";
    FlowScheduleOverlapPolicy["TerminateOther"] = "TERMINATE_OTHER";
    FlowScheduleOverlapPolicy["AllowAll"] = "ALLOW_ALL";
})(FlowScheduleOverlapPolicy || (FlowScheduleOverlapPolicy = {}));
export const FlowScheduleOverlapPolicySchema = z.enum(FlowScheduleOverlapPolicy);
/**
 * Body `POST /schedules` accepts.
 *
 * The last three are the initiator config every trigger carries, which governs the runs it starts
 * rather than when they start.
 */
export const FlowScheduledTriggerCreateBodySchema = z.object({
    flowId: z.uuid().describe('Flow the schedule belongs to.'),
    flowDefinitionId: z.uuid().describe('The definition each run executes.'),
    organizationId: z.uuid().describe('Organization the schedule belongs to.'),
    name: z.string().describe('Name the schedule is listed under.'),
    description: z.string().nullable().optional(),
    scheduleSpec: FlowScheduleSpecSchema.describe('When the schedule fires.'),
    overlapPolicy: FlowScheduleOverlapPolicySchema.describe('What to do when a run is still going.').optional(),
    catchupWindowMs: z.coerce
        .number()
        .nonnegative()
        .describe('How far back to make up runs missed while the service was down.')
        .optional(),
    pauseOnFailure: z.boolean().describe('Stop the schedule when a run fails.').optional(),
    isActive: z.boolean().describe('Whether the schedule is running.').optional(),
    maxFlowRunsPerMinute: z.coerce.number().positive().nullable().optional(),
    allowUnpublishedFlowRuns: z.boolean().nullable().optional(),
    desiredFlowRunPriority: z.coerce.number().nullable().optional(),
});
/**
 * Body `PATCH /schedules/{id}` accepts.
 *
 * None of the three ids is among them: a schedule that changed flow, definition or organization
 * would be a different schedule, and the Temporal schedule behind it is registered against these.
 */
export const FlowScheduledTriggerUpdateBodySchema = FlowScheduledTriggerCreateBodySchema.omit({
    flowId: true,
    flowDefinitionId: true,
    organizationId: true,
}).partial();
/** Path parameters the endpoints addressing one schedule accept. */
export const FlowScheduledTriggerIdParamsSchema = z.object({
    id: z.uuid().describe('The schedule.'),
});
/** Body the pause and unpause endpoints accept, both of which may be sent without one. */
export const FlowScheduledTriggerPauseBodySchema = z
    .object({
    note: z.string().describe('Why, recorded on the Temporal schedule.').optional(),
})
    .optional();
/** Query parameters `GET /schedules` accepts. */
export const FlowScheduledTriggerListQuerySchema = z.object({
    flowId: z.uuid().describe('Return only schedules of this flow.').optional(),
    flowDefinitionId: z
        .uuid()
        .describe('Return only schedules pinned to this definition.')
        .optional(),
    organizationId: OneOrManySchema(z.uuid())
        .describe('Return only schedules of this organization, or of any of these organizations.')
        .optional(),
    isActive: QueryBooleanSchema.describe('Return only running schedules, or with false only paused ones.').optional(),
    limit: FlowApiPageLimitSchema.describe('How many schedules to return.').optional(),
    offset: FlowApiPageOffsetSchema.describe('How many matches to skip, for paging.').optional(),
    orderBy: z.string().describe('Column to order by, such as name.').optional(),
    orderDirection: z.enum(['asc', 'desc']).describe('Direction to order by.').optional(),
});
//# sourceMappingURL=scheduledTrigger.js.map